import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getRouteTitleKey, isIdSegment } from '@/routes/routeTitles';
import { logger } from '@/lib/logger';
import { useVehicleLabel, useCustomerLabel, useInvoiceLabel } from '@/routes/paramHooks';
import { useActiveTeamId } from '@/contexts/TeamContext';

export interface BreadcrumbSegment {
  path: string;
  title: string;
  isLoading?: boolean;
}

/**
 * Hook to generate route-aware breadcrumb items with param hydration
 *
 * Features:
 * - Maps routes to human-readable titles
 * - Hydrates entity IDs with display names (e.g., "VW Golf (XX-123-Y)")
 * - Caches entity lookups via TanStack Query
 * - Gracefully handles loading and error states
 *
 * @example
 * // Static route
 * useBreadcrumb() // [{ path: '/vehicles', title: 'Vehicles' }]
 *
 * // Param route (loading)
 * useBreadcrumb() // [{ path: '/vehicles', title: 'Vehicles' }, { path: '/vehicles/abc', title: 'Vehicle (loading…)', isLoading: true }]
 *
 * // Param route (hydrated)
 * useBreadcrumb() // [{ path: '/vehicles', title: 'Vehicles' }, { path: '/vehicles/abc', title: 'VW Golf (XX-123-Y)' }]
 */
export function useBreadcrumb(): BreadcrumbSegment[] {
  const { pathname } = useLocation();
  const { t } = useTranslation('common');
  const teamId = useActiveTeamId();

  // Parse path to identify entity IDs
  const parts = pathname.split('/').filter(Boolean);

  // Identify entity IDs and their types
  let vehicleId: string | null = null;
  let customerId: string | null = null;
  let salesInvoiceId: string | null = null;
  let purchaseInvoiceId: string | null = null;

  let cursor = '';
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    cursor += `/${part}`;

    if (isIdSegment(part)) {
      const parentPath = cursor.substring(0, cursor.lastIndexOf('/'));

      if (parentPath === '/vehicles') {
        vehicleId = part;
      } else if (parentPath === '/customers') {
        customerId = part;
      } else if (parentPath === '/sales-invoices') {
        salesInvoiceId = part;
      } else if (parentPath === '/purchase-invoices') {
        purchaseInvoiceId = part;
      }
    }
  }

  // Call all hooks unconditionally (React Hooks rules)
  const vehicleLabelQuery = useVehicleLabel(vehicleId, teamId);
  const customerLabelQuery = useCustomerLabel(customerId, teamId);
  const salesInvoiceLabelQuery = useInvoiceLabel(salesInvoiceId, teamId, 'sales');
  const purchaseInvoiceLabelQuery = useInvoiceLabel(purchaseInvoiceId, teamId, 'purchase');

  // Build breadcrumb segments
  const segments: BreadcrumbSegment[] = [];
  cursor = '';

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    cursor += `/${part}`;

    if (isIdSegment(part)) {
      const parentPath = cursor.substring(0, cursor.lastIndexOf('/'));
      let title = part.slice(0, 8);
      let isLoading = false;

      // Use pre-fetched data for entity detail pages
      if (parentPath === '/vehicles' && vehicleId === part) {
        title = vehicleLabelQuery.data || t('breadcrumb.loading', 'Loading…');
        isLoading = vehicleLabelQuery.isLoading;
      } else if (parentPath === '/customers' && customerId === part) {
        title = customerLabelQuery.data || t('breadcrumb.loading', 'Loading…');
        isLoading = customerLabelQuery.isLoading;
      } else if (parentPath === '/sales-invoices' && salesInvoiceId === part) {
        title = salesInvoiceLabelQuery.data || t('breadcrumb.loading', 'Loading…');
        isLoading = salesInvoiceLabelQuery.isLoading;
      } else if (parentPath === '/purchase-invoices' && purchaseInvoiceId === part) {
        title = purchaseInvoiceLabelQuery.data || t('breadcrumb.loading', 'Loading…');
        isLoading = purchaseInvoiceLabelQuery.isLoading;
      } else {
        // Fallback: translate the route title key or use capitalized slug
        const titleKey = getRouteTitleKey(cursor);
        if (!titleKey && import.meta.env.DEV) {
          logger.warn(`[Breadcrumb] Missing route title for: ${cursor}`);
        }
        title = titleKey ? t(titleKey) : capitalize(part);
      }

      segments.push({ path: cursor, title, isLoading });
    } else {
      // Translate the route title key or use capitalized slug as fallback
      const titleKey = getRouteTitleKey(cursor);
      const title = titleKey ? t(titleKey) : capitalize(part);
      segments.push({ path: cursor, title });
    }
  }

  return segments.length > 0 ? segments : [{ path: '/', title: t('navigation.dashboard') }];
}

/**
 * Capitalize a string (helper)
 */
function capitalize(str: string): string {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
